import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Share2, Users, X } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader, { PageContainer } from '../components/PageHeader';
import { useSupervisors } from '../features/parentPortal/hooks/useSupervisors';
import { useSupervisionCode } from '../features/parentPortal/hooks/useSupervisionCode';
import { useSupervisionStatus } from '../features/parentPortal/supervisionStatus';
import { Skeleton, SkeletonCircle, SkeletonPanel, SkeletonRegion } from '../components/ui/Skeleton';
import ErrorBlock from '../features/parentPortal/components/ErrorBlock';
import Button from '../features/parentPortal/components/Button';
import ConfirmDialog from '../features/parentPortal/components/ConfirmDialog';
import SupervisionCodeCard from '../features/parentPortal/components/SupervisionCodeCard';
import { formatDate } from '../features/parentPortal/utils/format';
import '../styles/parentPortal.css';

export default function SupervisorsSettingsPage() {
  const navigate = useNavigate();
  const { supervises } = useSupervisionStatus();
  const { supervisors, loading, error, reload, revoke } = useSupervisors();
  const { code, expiresAt, generating, error: codeError, generate, reset } = useSupervisionCode();
  const [confirmRevoke, setConfirmRevoke] = useState(null);

  // `generate` rejects on failure (the hook also captures the message in its
  // own `error` state, rendered below) — this stops that rejection from
  // becoming an unhandled promise rejection when called directly from a
  // button's onClick, which was previously the only thing happening on
  // failure: no user-visible feedback at all.
  const handleGenerate = () => {
    generate().catch(() => {});
  };

  return (
    <>
      <SEO title="Account & Sharing" description="Manage who can see your learning on Graspd." path="/settings/sharing" noindex />
      <PageContainer style={{ maxWidth: 'var(--frame-prose)' }}>
        <PageHeader
          title="Sharing"
          subtitle="Decide who can see your learning, and invite a supervisor to follow along."
        />

        <section style={{ marginBottom: 'var(--space-6)' }}>
          <SectionHeading
            icon={<Share2 size={16} strokeWidth={1.5} />}
            title="Invite a supervisor"
            description="Generate a one-time code and give it to whoever will be watching your progress — a parent, a tutor, or a mentor."
          />
          {code ? (
            <SupervisionCodeCard
              code={code}
              expiresAt={expiresAt}
              generating={generating}
              onRegenerate={handleGenerate}
            />
          ) : (
            <div
              className="pp-card"
              style={{
                padding: 'var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                flexWrap: 'wrap',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-body-size)',
                  color: 'var(--text-secondary)',
                  maxWidth: 460,
                  lineHeight: 1.5,
                }}
              >
                Codes are 8 characters and single-use. Whoever will supervise you enters the code
                in their Family dashboard to link to your account.
              </p>
              <Button onClick={handleGenerate} loading={generating}>
                Generate code
              </Button>
            </div>
          )}
          {codeError && (
            <p style={{ margin: 'var(--space-2) 0 0', fontSize: 12, color: 'var(--danger)' }}>{codeError}</p>
          )}
          {code && (
            <div style={{ marginTop: 'var(--space-2)' }}>
              <button
                onClick={reset}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: 'var(--text-tertiary)',
                  fontSize: 12,
                  cursor: 'pointer',
                  padding: '4px 0',
                }}
              >
                Hide code
              </button>
            </div>
          )}
        </section>

        <section>
          <SectionHeading
            icon={<Eye size={16} strokeWidth={1.5} />}
            title="Who can see my learning"
            description="Linked supervisors can see your courses, progress, and transcript. Revoke access anytime."
          />

          {loading ? (
            <SkeletonRegion label="Loading supervisors">
              {Array.from({ length: 2 }, (_, i) => (
                <SkeletonPanel key={i} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-3)' }}>
                  <SkeletonCircle size={36} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Skeleton width="42%" height={14} style={{ marginBottom: 6 }} />
                    <Skeleton width="58%" height={12} />
                  </div>
                  <Skeleton width={72} height={30} />
                </SkeletonPanel>
              ))}
            </SkeletonRegion>
          ) : error ? (
            <ErrorBlock message={error} onRetry={reload} />
          ) : supervisors.length === 0 ? (
            <div
              className="pp-card"
              style={{
                border: '1px dashed var(--border-subtle)',
                boxShadow: 'none',
                padding: 'var(--space-6) var(--space-4)',
                textAlign: 'center',
                color: 'var(--text-secondary)',
                fontSize: 'var(--text-body-size)',
              }}
            >
              No one is currently supervising your account.
            </div>
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 'var(--space-2)' }}>
              {supervisors.map((s) => (
                <li
                  key={s.supervisorId}
                  className="pp-card-row"
                  style={{
                    padding: 'var(--space-3) var(--space-4)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontWeight: 'var(--text-panel-weight)',
                        fontSize: 'var(--text-panel-size)',
                        color: 'var(--text-primary)',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {s.displayName}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: 'var(--text-tertiary)',
                        marginTop: 2,
                      }}
                    >
                      {s.email}
                      {s.linkedAt ? ` · Linked ${formatDate(s.linkedAt)}` : ''}
                    </div>
                  </div>
                  <Button variant="secondary" size="sm" onClick={() => setConfirmRevoke(s)}>
                    <X size={14} strokeWidth={1.5} />
                    Revoke
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* The other direction: supervising someone else.
            The Parents nav item only appears once this account actually
            supervises a student (see Layout.jsx), so this is the durable
            entry point for a supervisor who hasn't linked anyone yet —
            without it, signing in on a later session would leave them no
            discoverable route to /family at all. Hidden once they do
            supervise someone, since the sidebar then carries it. */}
        {!supervises && (
          <section style={{ marginTop: 'var(--space-6)' }}>
            <SectionHeading
              icon={<Users size={16} strokeWidth={1.5} />}
              title="Supervising someone else?"
              description="If you're a parent, tutor, or mentor, you can follow a learner's progress from your own account. It's free — no subscription needed."
            />
            <div
              className="pp-card"
              style={{
                padding: 'var(--space-4)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                flexWrap: 'wrap',
              }}
            >
              <p
                style={{
                  margin: 0,
                  fontSize: 'var(--text-body-size)',
                  color: 'var(--text-secondary)',
                  maxWidth: 460,
                  lineHeight: 1.5,
                }}
              >
                Ask them to generate a supervision code from their own Sharing settings, then
                enter it to link their account.
              </p>
              <Button variant="secondary" onClick={() => navigate('/family')}>
                Link a learner
              </Button>
            </div>
          </section>
        )}
      </PageContainer>

      <ConfirmDialog
        open={!!confirmRevoke}
        onClose={() => setConfirmRevoke(null)}
        title={`Revoke ${confirmRevoke?.displayName || 'supervisor'}?`}
        message={`${confirmRevoke?.displayName || 'They'} will no longer see your courses or progress. You can share a new code with them anytime.`}
        confirmLabel="Revoke"
        onConfirm={async () => {
          if (!confirmRevoke) return;
          await revoke(confirmRevoke.supervisorId);
        }}
      />
    </>
  );
}

function SectionHeading({ icon, title, description }) {
  return (
    <div style={{ marginBottom: 'var(--space-3)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          color: 'var(--text-primary)',
          fontWeight: 'var(--text-section-weight)',
          fontSize: 'var(--text-section-size)',
          letterSpacing: 'var(--text-section-tracking)',
        }}
      >
        <span style={{ color: 'var(--text-secondary)' }}>{icon}</span>
        {title}
      </div>
      {description && (
        <p style={{ margin: '4px 0 0', fontSize: 'var(--text-body-size)', color: 'var(--text-tertiary)' }}>{description}</p>
      )}
    </div>
  );
}
